import { useList, useSelect } from "@pankod/refine-core";
import {
  Grid,
  useAutocomplete,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  TextField,
  FormControl,
  FormControlLabel,
  InputAdornment,
  Box,
  Button,
} from "@pankod/refine-mui";

import CustomTabPanel from "components/CustomTabPanel";
import useTabs from "hooks/useTabs";

import AutoGeneratedInputCmp from "components/auto-generated-input/AutoGeneratedInputCmp";
import { useEffect, useState } from "react";

export default function CompFields({
  register,
  control,
  errors,
  defaultLanguageRecord,
  getValues,
  setError,
  setValue,
  watch,
  disabled,
}) {
  const [tabValue, handleChange, defProps] = useTabs(0);
  const [accommodationTypes, setAccommodationTypes] = useState([]);

  const { options: struttureAC } = useSelect({
    resource: "strutture",
    pagination: {
      pageSize: 999,
    },
    optionLabel: "businessName", // -> servicesAC.label
    optionValue: "id", // -> servicesAC.value
    metaData: {
      fields: ["id", "businessName"],
    },
  });

  const { data: accTypes } = useList({
    resource: "accommodation-types",
    config: {
      pagination: {
        pageSize: 100,
      },
    },
  });

  useEffect(() => {
    if (accTypes) {
      //console.log(accTypes.data);
      setAccommodationTypes(accTypes.data);
      accTypes.data.forEach((item, index) => {
        setValue(`tariffe[${index}].accommodationTypeID`, item.id);
        new Array(6).fill(0).forEach((el, star) => {
          setValue(`tariffe[${index}].categories[${star}].stars`, star);
          new Array(3).fill(0).forEach((it, treatment) => {
            setValue(
              `tariffe[${index}].categories[${star}].treatments[${treatment}].name`,
              treatment == 0 ? "BB" : treatment == 1 ? "HB" : "FB"
            );
          });
        });
      });
    }
  }, [accTypes]);

  const fields = [
    {
      label: "Nome",
      name: "name",
      required: true,
      type: "text",
      localized: true,
    },
    {
      label: "Descrizione",
      name: "description",
      required: false,
      type: "text",
      lines: 4,
      localized: true,
    },
    {
      label: "Codice",
      name: "code",
      required: true,
      type: "text",
    },
    {
      label: "Data inizio evento",
      name: "start",
      required: true,
      type: "date",
      md: 6,
      disableDay: (day) => {
        return day > new Date(getValues("end"));
      },
      validation: (value) => {
        if (new Date(value) > new Date(getValues("end"))) {
          return "Range date non compatibile";
        }
      },
    },
    {
      label: "Data fine evento",
      name: "end",
      required: true,
      type: "date",
      md: 6,
      disableDay: (day) => {
        return day < new Date(getValues("start"));
      },
      validation: (value) => {
        if (new Date(value) < new Date(getValues("start"))) {
          return "Range date non compatibile";
        }
      },
    },
  ];

  const fieldsStrutture = [
    {
      label: "Strutture",
      name: "facilities",
      required: false,
      type: "multi-select-checkbox",
      options: struttureAC,
    },
  ];

  function InputField({ label, row, col, treatment }) {
    return (
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Typography variant="body">{label}</Typography>
        <TextField
          {...register(
            `tariffe[${row}].categories[${col}].treatments[${treatment}].value`
          )}
          variant="outlined"
          type="number"
          InputProps={{
            endAdornment: <InputAdornment position="end">€</InputAdornment>,
            style: {
              height: 30,
              margin: 6,
              minWidth: 30,
              fontSize: "12px",
            },
          }}
          inputProps={{
            style: {
              padding: 4,
              textAlign: "center",
            },
          }}
          defaultValue={0}
        />
      </Box>
    );
  }

  function PriceSetter({ row, col }) {
    return (
      <Grid container flexDirection="column" px={1}>
        <InputField label="BB" row={row} col={col} treatment={0} />
        <InputField label="HB" row={row} col={col} treatment={1} />
        <InputField label="FB" row={row} col={col} treatment={2} />
      </Grid>
    );
  }

  function AccTypeRow({ name, row }) {
    return (
      <TableRow sx={{ "& td": { borderBottomWidth: 2 } }}>
        <TableCell>
          <Typography variant="body">{name}</Typography>
        </TableCell>
        {new Array(6).fill(1).map((item, index) => (
          <TableCell key={index}>
            <PriceSetter row={row} col={index} />
          </TableCell>
        ))}
      </TableRow>
    );
  }

  return (
    <div>
      <Tabs
        value={tabValue}
        onChange={handleChange}
        //aria-label="basic tabs example"
      >
        <Tab label="Informazioni generali" {...defProps(0)} />
        <Tab label="Tariffe" {...defProps(1)} />
        <Tab label="Strutture associate" {...defProps(2)} />
      </Tabs>

      <CustomTabPanel value={tabValue} index={0}>
        <Grid container spacing={2}>
          {fields.map((field, index) => {
            return (
              <AutoGeneratedInputCmp
                key={index}
                disabled={disabled}
                {...field}
                register={register}
                control={control}
                errors={errors}
                setError={setError}
                fieldVariants="outlined"
                defaultValue={watch(field.name)}
                defaultLanguageRecord={defaultLanguageRecord}
                getValues={getValues}
                setValue={setValue}
                watch={watch}
              />
            );
          })}
        </Grid>
      </CustomTabPanel>

      <CustomTabPanel value={tabValue} index={1}>
        <TableContainer component={Paper} sx={{ p: 2 }}>
          <Table stickyHeader aria-label="collapsible table" padding="none">
            <TableHead style={{ height: 50 }}>
              <TableRow sx={{ "& th": { borderBottomWidth: 3 } }}>
                <TableCell align="center">
                  <Typography variant="h6">Tipologia</Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography variant="h6">NA</Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography variant="h6">1 stella</Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography variant="h6">2 stelle</Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography variant="h6">3 stelle</Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography variant="h6">4 stelle</Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography variant="h6">5 stelle</Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {accommodationTypes.length > 0 &&
                accommodationTypes?.map((item, index) => (
                  <AccTypeRow name={item.name} key={item.id} row={index} />
                ))}
            </TableBody>
          </Table>
          {/* <Button onClick={() => console.log(getValues("tariffe"))}>
            DEBUG
          </Button> */}
        </TableContainer>
      </CustomTabPanel>

      <CustomTabPanel value={tabValue} index={2}>
        <Grid container spacing={2}>
          {fieldsStrutture.map((field, index) => {
            return (
              <AutoGeneratedInputCmp
                key={index}
                disabled={disabled}
                {...field}
                register={register}
                control={control}
                errors={errors}
                setError={setError}
                fieldVariants="outlined"
                defaultValue={watch(field.name)}
                defaultLanguageRecord={defaultLanguageRecord}
                getValues={getValues}
                setValue={setValue}
                watch={watch}
              />
            );
          })}
        </Grid>
      </CustomTabPanel>
    </div>
  );
}
